import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Form, Button, Table, Alert, Spinner } from 'react-bootstrap';
import { Bar, Pie } from 'react-chartjs-2';
import { 
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { statisticsAPI, checkinsAPI, reportsAPI } from '../../utils/api';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const PersonnelStatistics = () => {
  const [timeRange, setTimeRange] = useState('week');
  const [customDate, setCustomDate] = useState("");
  const [siteVisits, setSiteVisits] = useState([]);
  const [totalHours, setTotalHours] = useState(0);
  const [visitsByDate, setVisitsByDate] = useState({});
  const [visitsBySite, setVisitsBySite] = useState({});
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState('');

  useEffect(() => {
    fetchStatistics(timeRange);
  }, [timeRange]);

  const handleExportPDF = async () => {
    setExportLoading(true);
    setExportError('');
    
    try {
      // Call the export API with the current time range
      const response = await statisticsAPI.exportPdf({ timeRange });
      
      // The response is already a blob since we specified responseType: 'blob' in the API call
      const blob = response.data;
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a link element
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `statistics_${timeRange}_${new Date().toISOString().split('T')[0]}.pdf`);
      
      // Append the link to the body
      document.body.appendChild(link);
      
      // Click the link to download the file
      link.click();
      
      // Remove the link from the body
      document.body.removeChild(link);
      
      // Clean up the URL object
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export PDF:', error);
      setExportError('Failed to export PDF. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  const fetchStatistics = async (range) => {
  try {
    setLoading(true);

    // Get stats including checkIns and reports
    const statsResponse = await statisticsAPI.getUserStats(range);

    let totalHoursCount = 0;
    const formattedVisits = [];

    const checkIns = statsResponse.data?.checkIns || [];

    checkIns.forEach(checkIn => {
      let duration = 0;
      let durationText = 'N/A';

      if (checkIn.checkInTime && checkIn.checkOutTime) {
        const checkInTime = new Date(checkIn.checkInTime);
        const checkOutTime = new Date(checkIn.checkOutTime);
        duration = (checkOutTime - checkInTime) / (1000 * 60 * 60);

        const hours = Math.floor(duration);
        const minutes = Math.round((duration - hours) * 60);
        durationText = `${hours}h ${minutes}m`;

        totalHoursCount += duration;
      }

      formattedVisits.push({
        id: checkIn.id,
        site: checkIn.site?.name || 'Unknown Site',
        siteId: checkIn.site?.id || null,
        checkIn: checkIn.checkInTime,
        checkOut: checkIn.checkOutTime,
        duration: durationText,
        report: checkIn.report?.content || 'N/A'
      });
    });


    const totalHoursValue = statsResponse?.data?.stats?.totalHours || totalHoursCount;

    setSiteVisits(formattedVisits);
    setTotalHours(totalHoursValue);

    // Build chart data
    const dateData = {};
    const siteData = {};

    formattedVisits.forEach(visit => {
      const date = new Date(visit.checkIn).toLocaleDateString();
      dateData[date] = (dateData[date] || 0) + 1;
      siteData[visit.site] = (siteData[visit.site] || 0) + 1;
    });

    setVisitsByDate(dateData);
    setVisitsBySite(siteData);
    setLoading(false);
  } catch (error) {
    console.error('Failed to fetch statistics:', error);
    setLoading(false);
  }
};


  const barChartData = {
    labels: Object.keys(visitsByDate),
    datasets: [
      {
        label: 'Site Visits',
        data: Object.values(visitsByDate),
        backgroundColor: 'rgba(54, 162, 235, 0.5)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      },
    ],
  };

  const pieChartData = {
    labels: Object.keys(visitsBySite),
    datasets: [
      {
        label: 'Visits by Site',
        data: Object.values(visitsBySite),
        backgroundColor: [
          'rgba(255, 99, 132, 0.5)',
          'rgba(54, 162, 235, 0.5)',
          'rgba(255, 206, 86, 0.5)',
          'rgba(75, 192, 192, 0.5)',
          'rgba(153, 102, 255, 0.5)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  return (
    <Container fluid className="py-3 py-md-4">
      <Row className="mb-3 mb-md-4">
        <Col xs={12} md={8}>
          <h1 className="mb-2 mb-md-4">Statistics</h1>
        </Col>
        <Col xs={12} md={4} className="d-flex justify-content-start justify-content-md-end align-items-md-center">
          <Form.Group className="w-100 w-md-auto mb-2 mb-md-0" style={{ position: 'relative' }}>
            <Form.Select
              value={timeRange === 'custom' ? 'custom' : (/^\d{4}-\d{2}-\d{2}$/.test(timeRange) ? 'custom' : timeRange)}
              onChange={e => {
                if (e.target.value === 'custom') {
                  setTimeRange('custom');
                } else {
                  setTimeRange(e.target.value);
                  setCustomDate("");
                }
              }}
            >
              <option value="day">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
              <option value="custom">Custom Date...</option>
            </Form.Select>
            {timeRange === 'custom' && (
              <div style={{ marginTop: '8px' }}>
                <Form.Label htmlFor="custom-date-picker">Select Date</Form.Label>
                <Form.Control
                  id="custom-date-picker"
                  type="date"
                  value={customDate}
                  onChange={e => {
                    setCustomDate(e.target.value);
                  }}
                  style={{ minWidth: '140px' }}
                />
              </div>
            )}
          </Form.Group>
        </Col>
      </Row>
      
      {/* Summary Card */}
      <Card className="mb-3 mb-md-4 dashboard-card">
        <Card.Body>
          <Card.Title className="mb-3">Summary</Card.Title>
          <Row>
            <Col xs={12} sm={4} className="mb-3 mb-sm-0">
              <div className="text-center">
                <h3 className="h4">{siteVisits.length}</h3>
                <p className="text-muted mb-0 small">Total Site Visits</p>
              </div>
            </Col>
            <Col xs={12} sm={4} className="mb-3 mb-sm-0">
              <div className="text-center">
               <h3 className="h4">{Number(totalHours || 0).toFixed(1)}</h3>
                <p className="text-muted mb-0 small">Total Hours</p>
              </div>
            </Col>
            <Col xs={12} sm={4}>
              <div className="text-center">
                <h3 className="h4">{Object.keys(visitsBySite).length}</h3>
                <p className="text-muted mb-0 small">Unique Sites</p>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      {/* Charts */}
      <Row className="mb-3 mb-md-4 g-3 g-md-4 align-items-stretch">
        <Col xs={12} lg={7} className="mb-3 mb-lg-0">
          <Card className="chart-container h-100">
            <Card.Body>
              <Card.Title className="mb-3">Visits by Date</Card.Title>
              <div style={{ height: '250px', minHeight: '250px' }}>
                <Bar 
                  data={barChartData} 
                  options={{
                    maintainAspectRatio: false,
                    responsive: true,
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          stepSize: 1
                        }
                      }
                    },
                    plugins: {
                      legend: {
                        display: window.innerWidth > 768
                      }
                    }
                  }}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} lg={5}>
          <Card className="chart-container h-100">
            <Card.Body>
              <Card.Title className="mb-3">Visits by Site</Card.Title>
              <div style={{ height: '250px', minHeight: '250px', display: 'flex', justifyContent: 'center' }}>
                <Pie 
                  data={pieChartData} 
                  options={{
                    maintainAspectRatio: false,
                    responsive: true,
                    plugins: {
                      legend: {
                        display: window.innerWidth > 768,
                        position: 'bottom'
                      }
                    }
                  }}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Detailed Visit Table */}
      <Card className="table-container">
        <Card.Body>
          <Card.Title className="mb-3">Detailed Site Visits</Card.Title>
          <div className="table-responsive">
            <Table striped hover className="mb-0">
              <thead>
                <tr>
                  <th>Site</th>
                  <th className="d-none d-md-table-cell">Check In</th>
                  <th className="d-none d-lg-table-cell">Check Out</th>
                  <th className="d-none d-sm-table-cell">Duration</th>
                  <th className="d-none d-xl-table-cell">Report</th>
                </tr>
              </thead>
              <tbody>
                {siteVisits.map(visit => (
                  <tr key={visit.id}>
                    <td>
                      <div>
                        <div className="fw-bold">{visit.site}</div>
                        <small className="text-muted d-md-none">
                          {new Date(visit.checkIn).toLocaleDateString()}
                        </small>
                      </div>
                    </td>
                    <td className="d-none d-md-table-cell">{new Date(visit.checkIn).toLocaleString()}</td>
                    <td className="d-none d-lg-table-cell">{new Date(visit.checkOut).toLocaleString()}</td>
                    <td className="d-none d-sm-table-cell">{visit.duration}</td>
                    <td className="d-none d-xl-table-cell">
                      {visit.report && visit.report.length > 30 ? `${visit.report.substring(0, 30)}...` : visit.report}
                    </td>
                  </tr>
                ))}
                {siteVisits.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center">No site visits found for the selected time range.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
          <div className="d-flex justify-content-center mt-3 flex-column flex-md-row gap-2">
            {exportError && (
              <Alert variant="danger" className="mb-0 py-2 px-3">
                {exportError}
              </Alert>
            )}
            <Button 
              variant="outline-primary" 
              onClick={handleExportPDF} 
              disabled={exportLoading || siteVisits.length === 0}
              className="w-100 w-md-auto"
            >
              {exportLoading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                  Exporting...
                </>
              ) : (
                'Export as PDF'
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default PersonnelStatistics;
